import { type IDataObject, type ISuggestion, type IUserProfile, type IUserTenantAccess, type OrderBy, queryBuilder, type ROLES } from '@inpart-io/devtools-iap';
import { GET_BY_ID, GET_BY_ID_LIST, RETRIEVE_BY_ID_LIST, RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import { type IUserGroup, userGroupsManager, userProfilesManager, userTenantAccessesManager } from '@inpart-io/padme-iap';
import { userGroupProperties, userProfileProperties, userTenantAccessProperties } from '@inpart-io/padme-iap';
import { useStore } from 'vuex';

import { USER_GROUP_MODULE } from '@/store/user-group-module';
import { USER_PROFILE_MODULE } from '@/store/user-profile-module';
import { RETRIEVE_USER_PROFILES, RETRIEVE_USER_PROFILES_WITH_TENANT_ACCESS } from '@/store/user-profile-module/actions';
import { GET_FROM_USER } from '@/store/user-profile-module/getters';
import { USER_TENANT_ACCESS_MODULE } from '@/store/user-tenant-access-module';

export function useUserProfileHelper(): {
    searchUserProfiles: (
        searchTerm: string,
        applicationRoles: ROLES[],
        userIsInIds: number[],
        userIsNotInIds?: number[],
        hideDisabledUser?: boolean,
        maxResults?: number
    ) => Promise<IUserProfile[]>;
    loadUserProfiles: (uniqueUserIds: number[]) => Promise<number[]>;
    loadUserGroups: (uniqueUserGroupIds: number[]) => Promise<number[]>;
    loadUserProfilesWithTenantAccess: (uniqueUserIds: number[], hideDisabledUser?: boolean) => Promise<IUserProfile[]>;
    searchUserTenantAccessAndGroups: (
        searchTerm: string,
        applicationRoles: ROLES[],
        userIsInIds: number[],
        userIsNotInIds?: number[],
        hideDisabledUser?: boolean,
        groupIsInIds?: number[],
        groupIsNotInIds?: number[],
        maxResults?: number
    ) => Promise<Array<IUserTenantAccess | IUserGroup>>;
    loadUserProfilesByUserTenantAccess: (ids: number[]) => Promise<any[]>;
} {
    const store = useStore();

    /**
     * Utility method to execute a search or typeahead request for User profiles,
     *
     * @param {string} searchTerm Mandatory. The search term.
     * @param {number[]} applicationRoles The list of Applications Role Ids the User must have.
     * @param {number[]} userIsInIds Optional. The list of ids of which the user should be part of.
     * @param {number[]} userIsNotInIds Optional. The list of ids of which the user should NOT be part of.
     * @param {boolean} hideDisabledUser Optional. TRUE by default, dictates if the query should hide disable users.
     * @param {number } maxResults Optional. 10 by default. The max number of result for each query.
     * @return {*} {Promise<IUserProfile[]>}
     */
    async function searchUserProfiles(
        searchTerm: string,
        applicationRoles: number[],
        userIsInIds?: number[],
        userIsNotInIds?: number[],
        hideDisabledUser = true,
        maxResults = 10
    ): Promise<IUserProfile[]> {
        if (searchTerm) {
            return userProfilesManager.typeaheadUserProfilesInTenant(searchTerm, applicationRoles, userIsInIds, userIsNotInIds, hideDisabledUser, maxResults);
        } else {
            return (
                await store.dispatch(`${USER_PROFILE_MODULE}/${RETRIEVE_USER_PROFILES}`, {
                    applicationRoles,
                    userIsInIds,
                    userIsNotInIds,
                    hideDisabledUser
                })
            ).map((dobjId: number) => {
                return store.getters[`${USER_PROFILE_MODULE}/${GET_BY_ID}`](dobjId);
            });
        }
    }

    /**
     * Utility method to retrieve User profiles that aren't yet loaded in the local store
     * @param {number[]} uniqueUserIds
     * @returns {*} {Promise<number[]>} User profile ids
     */
    async function loadUserProfiles(uniqueUserIds: number[]): Promise<number[]> {
        const notFoundUserProfileIds = [] as number[];
        const foundUserProfileIds = [] as number[];

        uniqueUserIds.forEach((userId) => {
            if (userId) {
                const userProfile: IUserProfile = store.getters[`${USER_PROFILE_MODULE}/${GET_FROM_USER}`](userId);
                if (userProfile === undefined || userProfile === null) {
                    notFoundUserProfileIds.push(userId);
                } else {
                    foundUserProfileIds.push(userProfile.dobjId);
                }
            }
        });
        let searchedUserProfileIds: number[] = [];
        if (notFoundUserProfileIds.length > 0) {
            searchedUserProfileIds = await store.dispatch(`${USER_PROFILE_MODULE}/${RETRIEVE_BY_QUERY}`, {
                query: queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.isIn('user', notFoundUserProfileIds))),
                properties: userProfileProperties.DEFAULT
            });
        }
        foundUserProfileIds.push(...searchedUserProfileIds);
        return Array.from(new Set(foundUserProfileIds));
    }

    async function loadUserProfilesByUserTenantAccess(ids: number[]): Promise<IUserTenantAccess[]> {
        const searchedUserProfileIds = await userTenantAccessesManager.getByIdList(ids);

        return searchedUserProfileIds;
    }

    /**
     * Utility method to retrieve User groups that aren't yet loaded in the local store
     * @param {number[]} uniqueUserGroupIds
     * @returns {*} {Promise<number[]>} User group ids
     */
    async function loadUserGroups(uniqueUserGroupIds: number[]): Promise<number[]> {
        const notFoundUserGroupIds = [] as number[];
        const foundUserGroupIds = [] as number[];
        uniqueUserGroupIds.forEach((groupId) => {
            const userGroup: IUserGroup = store.getters[`${USER_GROUP_MODULE}/${GET_BY_ID}`](groupId);
            if (userGroup === undefined || userGroup === null) {
                notFoundUserGroupIds.push(groupId);
            } else {
                foundUserGroupIds.push(userGroup.dobjId);
            }
        });
        let searchedUserGroupIds: number[] = [];
        if (notFoundUserGroupIds.length > 0) {
            searchedUserGroupIds = await store.dispatch(`${USER_GROUP_MODULE}/${RETRIEVE_BY_QUERY}`, {
                query: queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.isIn('dobjId', notFoundUserGroupIds))),
                properties: userGroupProperties.DEFAULT
            });
        }
        foundUserGroupIds.push(...searchedUserGroupIds);
        const foundUserGroups = store.getters[`${USER_GROUP_MODULE}/${GET_BY_ID_LIST}`](foundUserGroupIds);
        const userPromises: Array<Promise<void>> = [];
        foundUserGroups.forEach((group: IUserGroup) => {
            userPromises.push(
                store.dispatch(`${USER_TENANT_ACCESS_MODULE}/${RETRIEVE_BY_ID_LIST}`, {
                    idList: group.members,
                    properties: userTenantAccessProperties.DEFAULT
                })
            );
        });
        await Promise.all(userPromises);
        return Array.from(new Set(foundUserGroupIds));
    }

    /**
     * Utility method to retrieve User profiles with Tenant access
     * @param {number[]} uniqueUserIds
     * @returns {*} {Promise<number[]>} User profile ids
     */
    async function loadUserProfilesWithTenantAccess(uniqueUserIds: number[], hideDisabledUser?: boolean): Promise<IUserProfile[]> {
        if (uniqueUserIds?.length === 0) {
            return [];
        }
        const searchedUserProfileIds: IUserProfile[] = (
            await store.dispatch(`${USER_PROFILE_MODULE}/${RETRIEVE_USER_PROFILES_WITH_TENANT_ACCESS}`, {
                userIsInIds: uniqueUserIds,
                hideDisabledUser
            })
        )?.map((dobjId: number) => {
            return store.getters[`${USER_PROFILE_MODULE}/${GET_BY_ID}`](dobjId);
        });
        return Array.from(new Set(searchedUserProfileIds));
    }

    /**
     * Utility method to execute a search or typeAhead request for a mix of user tenant accesses and groups,
     *
     * @param {string} searchTerm Mandatory. The search term.
     * @param {number[]} applicationRoles The list of Applications Role Ids the User must have.
     * @param {number[]} userIsInIds Optional. The list of ids of which the user should be part of.
     * @param {number[]} userIsNotInIds Optional. The list of ids of which the user should NOT be part of.
     * @param {number[]} groupIsInIds Optional. The list of ids of which the group should be part of.
     * @param {number[]} groupIsNotInIds Optional. The list of ids of which the group should NOT be part of.
     * @param {boolean} hideDisabledUser Optional. TRUE by default, dictates if the query should hide disable users.
     * @param {number } maxResults Optional. 10 by default. The max number of result for each query.
     * @return {*} {Promise<Array<IUserTenantAccess | IUserGroup>>}
     */
    async function searchUserTenantAccessAndGroups(
        searchTerm: string,
        applicationRoles: number[],
        userIsInIds?: number[],
        userIsNotInIds?: number[],
        hideDisabledUser = true,
        groupIsInIds?: number[],
        groupIsNotInIds?: number[],
        maxResults = 5
    ): Promise<Array<IUserTenantAccess | IUserGroup>> {
        const _operator = await userProfilesManager.getUserProfileOperators(applicationRoles, userIsInIds, userIsNotInIds, hideDisabledUser);
        const _groupOperators = [];
        const isUserRequestNeeded = !Array.isArray(userIsInIds) || userIsInIds.length;
        const isGroupRequestNeeded = !Array.isArray(groupIsInIds) || groupIsInIds.length;

        if (groupIsInIds?.length > 0) {
            _groupOperators.push(queryBuilder.cmp.isIn('dobjId', groupIsInIds));
        }

        if (groupIsNotInIds?.length > 0) {
            _groupOperators.push(queryBuilder.cmp.isNotIn('dobjId', groupIsNotInIds));
        }
        if (searchTerm) {
            let userTenantAccessSuggestions: Array<ISuggestion<IDataObject>> = [];

            if (isUserRequestNeeded) {
                userTenantAccessSuggestions = await userTenantAccessesManager.typeahead(searchTerm, maxResults, _operator, userTenantAccessProperties.DEFAULT);
            }

            let userGroupSuggestions: Array<ISuggestion<IDataObject>> = [];

            if (isGroupRequestNeeded) {
                userGroupSuggestions = await userGroupsManager.typeahead(
                    searchTerm,
                    maxResults,
                    queryBuilder.op.and(..._groupOperators),
                    userGroupProperties.DEFAULT
                );
            }

            const suggestions = [...userGroupSuggestions, ...userTenantAccessSuggestions].sort((a, b) => {
                return b.score - a.score;
            });

            return suggestions?.map((typeaheadResult: ISuggestion<IDataObject>) => {
                return typeaheadResult.dataObject as IUserTenantAccess | IUserGroup;
            });
        } else {
            const orderUserBy: OrderBy[] = [
                { attribute: `user.@(:UserProfile:user).lastName`, direction: 'ASC' },
                { attribute: `user.@(:UserProfile:user).firstName`, direction: 'ASC' }
            ];
            let userTenantAccessSuggestionIds: number[] = [];

            if (isUserRequestNeeded) {
                userTenantAccessSuggestionIds = await store.dispatch(`${USER_TENANT_ACCESS_MODULE}/${RETRIEVE_BY_QUERY}`, {
                    query: queryBuilder.build(queryBuilder.op.and(_operator), 0, maxResults, orderUserBy),
                    properties: userTenantAccessProperties.DEFAULT
                });
            }

            const orderGroupBy: OrderBy[] = [{ attribute: 'name', direction: 'ASC' }];
            let userGroupSuggestionIds: number[] = [];

            if (isGroupRequestNeeded) {
                userGroupSuggestionIds = await store.dispatch(`${USER_GROUP_MODULE}/${RETRIEVE_BY_QUERY}`, {
                    query: queryBuilder.build(queryBuilder.op.and(..._groupOperators), 0, maxResults, orderGroupBy),
                    properties: userGroupProperties.DEFAULT
                });
            }

            return [
                ...store.getters[`${USER_TENANT_ACCESS_MODULE}/${GET_BY_ID_LIST}`](userTenantAccessSuggestionIds),
                ...store.getters[`${USER_GROUP_MODULE}/${GET_BY_ID_LIST}`](userGroupSuggestionIds)
            ];
        }
    }

    return {
        searchUserProfiles,
        loadUserProfiles,
        loadUserProfilesByUserTenantAccess,
        loadUserGroups,
        loadUserProfilesWithTenantAccess,
        searchUserTenantAccessAndGroups
    };
}
